import React, { useEffect, useState, useRef, useContext } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useFirestore } from "../firebase/FirestoreContext";
import { EnvContext } from "../context/EnvContext";
import config from "../config";
import { fontWeight, positions } from "@mui/system";

const breakpoints = [755]
const mq = breakpoints.map(bp => `@media (min-width: ${bp}px)`)

const FeaturedCarousel_Knowledge = ({ featuredCollection, containerStyles }) => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const collectionPath = environment === "staging" ? config.environment.staging.collectionPath : config.environment.production.collectionPath;

  const [currentIndex, setCurrentIndex] = useState(0);
  const slideInterval = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const isAutoScrolling = useRef(false);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasContent, setHasContent] = useState(true);
  const [height, setHeight] = useState(180);
  const [carouselReady, setCarouselReady] = useState(false);

  useEffect(() => {
    const fetchFeaturedArticles = async () => {
      try {
        setLoading(true);
        const knowledgeRef = collection(db, `${collectionPath}Help/Knowledge/DataBase`);
        console.log("Knowledge Ref Path:", knowledgeRef.path); // Log the collection path
        const q = query(knowledgeRef, where("featured", "==", true)); // Remove orderBy
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          console.warn("No documents found in the collection.");
          setHasContent(false);
        } else {
          const articles = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Sort the articles by sortOrder in JavaScript
          articles.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1));

          setFeaturedArticles(articles);
          setHasContent(articles.length > 0);
        }
      } catch (error) {
        console.error("Error fetching featured articles:", error);
        setHasContent(false);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedArticles();
  }, [db, collectionPath]);

  const repeatedArticles = featuredArticles.length > 1 ? Array(20).fill(featuredArticles).flat() : featuredArticles;

  const startAutoSlide = () => {
    if (featuredArticles.length <= 1) return;

    slideInterval.current = setInterval(() => {
      isAutoScrolling.current = true;
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }, 5000);
  };

  const resetAutoSlide = () => {
    if (slideInterval.current) clearInterval(slideInterval.current);
    startAutoSlide();
  };

  useEffect(() => {
    if (featuredArticles.length > 1) {
      startAutoSlide();
    }
    return () => clearInterval(slideInterval.current);
  }, [featuredArticles]);

  useEffect(() => {
    if (featuredArticles.length === 0 || !containerRef.current || !containerRef.current.firstChild) {
      console.warn("No articles or container is not ready. Skipping scrollTo.");
      return;
    }

    const cardWidth = containerRef.current.firstChild.offsetWidth || 0;

    containerRef.current.scrollTo({
      left: currentIndex * cardWidth,
      behavior: isAutoScrolling.current ? "smooth" : "instant",
    });

    const timer = setTimeout(() => {
      isAutoScrolling.current = false;
      setCarouselReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [currentIndex, featuredArticles]);

  useEffect(() => {
    const handleScroll = () => {
      if (isAutoScrolling.current || featuredArticles.length === 0) return;

      if (containerRef?.current) {
        const cardWidth = containerRef?.current?.firstChild?.offsetWidth || 0;
        const newIndex = Math.round(containerRef?.current?.scrollLeft / cardWidth);

        setCurrentIndex(newIndex);
        resetAutoSlide();
      }
    };

    const container = containerRef?.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => container?.removeEventListener("scroll", handleScroll);
  }, [featuredArticles]);

  useEffect(() => {
    if (featuredArticles.length === 0 || !containerRef.current || !containerRef.current.firstChild) {
      return;
    }

    const middleIndex = Math.floor(repeatedArticles.length / 2);
    if (currentIndex === 0 || currentIndex === repeatedArticles.length - 1) {
      setTimeout(() => {
        if (containerRef.current == null) return;

        const cardWidth = containerRef.current.firstChild.offsetWidth || 0;
        containerRef.current.scrollTo({
          left: middleIndex * cardWidth,
          behavior: "instant",
        });
        setCurrentIndex(middleIndex);
      }, 500);
    }
  }, [currentIndex, repeatedArticles.length, featuredArticles]);

  const handleDotClick = (index) => {
    if (featuredArticles.length === 0) return;

    const middleIndex = Math.floor(repeatedArticles.length / 2);
    setCurrentIndex(middleIndex + index);
    resetAutoSlide();
  };

  if (loading) {
    return <div style={{ textAlign: "center", marginTop: "20px", height: height, background: "#f5f5f5" }}></div>;
  }

  if (!hasContent) {
    return <div style={{ textAlign: "center", marginTop: "20px" }}>No featured articles found.</div>;
  }

  return (
    <div css={{
      ...containerStyles, marginBottom: 20, [mq[0]]: {
        // maxWidth: 570,
        // maxHeight: 300,
      }
    }}>
      <div
        ref={containerRef}
        style={{
          ...styles.carouselContainer,
          padding: featuredArticles.length === 1 ? "0px 10px" : "0",
          visibility: carouselReady ? "visible" : "hidden",
        }}
      >
        {repeatedArticles.map((article, index) => (
          <div
            key={index}
            onClick={() => {
              navigate(`/knowledge/${article.customURL}`);
            }}
            style={{
              ...styles.card.container,
              flex: featuredArticles.length === 1 ? "0 0 100%" : "0 0 100%",
              maxWidth: "100%",
            }}
          >
            <div style={{ display: "flex", width: "100%", position: "relative", flexDirection: "column" }}>
              <div
                style={{
                  ...styles.card.card,
                }}
              >
                <img loading="lazy" src={article.image || "https://placehold.co/800x450"} alt={article.title} css={{ ...styles.image }} />
                <div style={{ ...styles.card.title }}>{article?.title}</div>
              </div>
              <div style={{ padding: "10px 5px 5px 5px", fontSize: ".9rem" }}>{article.shortDescription}</div>
              <div style={{ padding: "0px 5px", fontSize: ".9rem", fontWeight: "600", textDecoration: "underline", color: "black" }}>Read More...</div>
            </div>
          </div>
        ))}
      </div>
      {featuredArticles.length > 1 && (
        <div style={styles.dotsContainer}>
          {featuredArticles.map((_, index) => {
            const middleIndex = Math.floor(repeatedArticles.length / 2);
            const isActive = currentIndex % featuredArticles.length === index;

            return (
              <div
                key={index}
                onClick={() => handleDotClick(index)}
                style={{
                  ...styles.dot,
                  backgroundColor: isActive ? "black" : "#b2b2b2",
                  opacity: isActive ? 1 : 0.5,
                }}
              ></div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeaturedCarousel_Knowledge;

const styles = {
  title: {
    fontSize: 12,
    fontWeight: 600,
  },
  carouselContainer: {
    display: "flex",
    overflowX: "auto",
    scrollBehavior: "smooth",
    scrollSnapType: "x mandatory",
    gap: "10px",
    maxWidth: "100%",
    scrollbarWidth: "none",
  },
  card: {
    title: {
      position: "absolute",
      left: 0,
      bottom: 0,
      padding: "15px",
      color: "white",
      fontWeight: 600,
      fontSize: "1.2rem",
      // shadow
      textShadow: "2px 2px 10px rgba(0, 0, 0, 0.4)",
    },
    container: {
      scrollSnapAlign: "center",
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      boxSizing: `border-box`,
      width: "100%",
    },
    card: {
      position: "relative",
      overflow: "hidden",
      borderRadius: "12px",
      border: "1px solid #e0e0e0",
      background: "#fafafa",
      height: "100%",
      display: "flex",
    },
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    zIndex: 0,
    position: "relative",
    aspectRatio: "1.25/1",
    maxHeight: 200,
    [mq[0]]: {
      maxHeight: 400,
    }
  },
  loaderOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    color: "white",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    zIndex: -100,
    opacity: 0.1,
  },
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "5px",
    paddingTop: "10px",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    cursor: "pointer",
  },
};
